/**
 * ManifestDoPortal — troca o manifest PWA ativo quando a navegação está em
 * /portal (PR4 Task 6: app instalado e push do parceiro).
 *
 * `V1/index.html` linka `/manifest.json` (o app interno) por padrão. Sem
 * isso, "Adicionar à tela inicial" dentro do Portal do Parceiro instalaria o
 * ícone/nome do SE_WEB interno em vez do "Ponto Banese — Portal do Parceiro"
 * — o parceiro nunca deveria ver a marca interna. Como o manifest só é lido
 * pelo browser no momento do prompt de instalação (não é recurso
 * bloqueante), trocar o `href` do `<link rel="manifest">` em tempo de
 * navegação — via DOM, fora do React — é suficiente e não precisa de SSR.
 *
 * Mesmo raciocínio para `apple-mobile-web-app-title`: é o nome que o iOS usa
 * embaixo do ícone quando instalado via Safari (que ignora `manifest.json`).
 *
 * M4 (revisão final do PR 4) — mesmo raciocínio, mais dois: `apple-touch-icon`
 * (o Safari/iOS PRIORIZA esse `<link>` sobre os ícones do manifest.json — sem
 * trocar, o ícone na tela inicial do parceiro seria o "S" interno) e
 * `theme-color` (a cor da barra de status/moldura do PWA). "O parceiro nunca
 * deveria ver a marca interna" vale tanto pro ícone quanto pra cor.
 *
 * Peça K (D3, passo 19): também o `document.title` — "Portal do Parceiro — Ponto Banese", nunca "SE_WEB".
 *
 * Montado uma única vez em App.tsx, fora de <Routes> (para não remontar a
 * cada troca de rota) mas dentro do Router (precisa de useLocation).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const MANIFEST_HREF_PORTAL = "/manifest-portal.json";
const APPLE_TITLE_PORTAL = "Ponto Banese";
const APPLE_ICON_PORTAL = "/icons/portal-180.png";
const THEME_COLOR_PORTAL = "#047857"; // == theme_color do manifest-portal.json
const TITULO_DO_PORTAL = "Portal do Parceiro — Ponto Banese";

export function ManifestDoPortal() {
  const location = useLocation();

  // Valores originais (o que V1/index.html já linkava) lidos SÓ na 1ª
  // execução do efeito — nunca sobrescritos depois, mesmo que a navegação
  // já comece dentro de /portal. É pra esses valores que a gente restaura
  // ao sair do /portal.
  const originalManifestHref = useRef<string | null>(null);
  const originalAppleTitle = useRef<string | null>(null);
  const originalAppleIcon = useRef<string | null>(null);
  const originalThemeColor = useRef<string | null>(null);
  const originalTitle = useRef<string | null>(null);
  const capturouOriginais = useRef(false);
  // Peça K: o título só é trocado quando a rota ENTRA ou SAI de /portal. Dentro do portal o contador "(N)" do
  // `tituloDaAba` mora no `document.title`: reescrevê-lo a cada navegação apagaria o contador.
  const estavaEmPortal = useRef<boolean | null>(null);

  useEffect(() => {
    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const appleTitleMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]'
    );
    const appleIconLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    if (!capturouOriginais.current) {
      originalManifestHref.current = manifestLink?.getAttribute("href") ?? null;
      originalAppleTitle.current = appleTitleMeta?.getAttribute("content") ?? null;
      originalAppleIcon.current = appleIconLink?.getAttribute("href") ?? null;
      originalThemeColor.current = themeColorMeta?.getAttribute("content") ?? null;
      originalTitle.current = document.title;
      capturouOriginais.current = true;
    }

    const emPortal = location.pathname.startsWith("/portal");

    if (manifestLink) {
      const alvo = emPortal ? MANIFEST_HREF_PORTAL : originalManifestHref.current;
      if (alvo) manifestLink.setAttribute("href", alvo);
    }

    // apple-mobile-web-app-title: troca só "se existir" (defensivo — a meta
    // sempre existe em produção via index.html, mas nada aqui deve quebrar
    // se ela não estiver no DOM).
    if (appleTitleMeta) {
      const alvo = emPortal ? APPLE_TITLE_PORTAL : originalAppleTitle.current;
      if (alvo) appleTitleMeta.setAttribute("content", alvo);
    }

    // apple-touch-icon (M4): mesma troca defensiva — o Safari/iOS lê este
    // link, não o manifest.json, pro ícone da tela inicial.
    if (appleIconLink) {
      const alvo = emPortal ? APPLE_ICON_PORTAL : originalAppleIcon.current;
      if (alvo) appleIconLink.setAttribute("href", alvo);
    }

    // theme-color (M4): cor da barra de status/moldura do PWA instalado.
    if (themeColorMeta) {
      const alvo = emPortal ? THEME_COLOR_PORTAL : originalThemeColor.current;
      if (alvo) themeColorMeta.setAttribute("content", alvo);
    }

    if (estavaEmPortal.current !== emPortal) {
      document.title = emPortal ? TITULO_DO_PORTAL : (originalTitle.current ?? document.title);
      estavaEmPortal.current = emPortal;
    }
  }, [location.pathname]);

  return null;
}

export default ManifestDoPortal;
